import { Box, type BoxProps, type ResponsiveStyleValue } from '@mui/system';
import type { Options } from 'qr-code-styling';
import { useEffect, useState } from 'react';

const defaultOptions: Options = {
  width: 240,
  height: 240,
  margin: 0,
  cornersSquareOptions: {
    type: 'extra-rounded',
  },
  dotsOptions: {
    type: 'rounded',
  },
  backgroundOptions: {
    color: 'transparent',
  },
};

export interface QRCodeProps extends Omit<BoxProps<'img'>, 'width' | 'height'> {
  data: string;
  size: ResponsiveStyleValue<number>;
  options?: Omit<Options, 'data' | 'width' | 'height'>;
  isOnDark?: boolean;
}

function getMaxSize(size: ResponsiveStyleValue<number>): number {
  if (typeof size === 'number') return size;
  if (Array.isArray(size)) {
    const numbers = size.filter((v): v is number => typeof v === 'number');
    return numbers.length ? Math.max(...numbers) : 240;
  }
  if (size && typeof size === 'object') {
    const numbers = Object.values(size).filter(
      (v): v is number => typeof v === 'number'
    );
    return numbers.length ? Math.max(...numbers) : 240;
  }
  return 240;
}

export default function QRCode({
  data,
  size,
  options,
  isOnDark = false,
  ...imgProps
}: QRCodeProps) {
  const [dataURL, setDataURL] = useState<string | undefined>();
  const maxSize = getMaxSize(size);
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    // generating 3x image to make sure rounded corners look good on high resolution displays
    width: maxSize * 3,
    height: maxSize * 3,
    data,
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: only regenerate when inputs change
  useEffect(() => {
    const createQRCode = async () => {
      const QR = (await import('qr-code-styling')).default;
      const qrCode = new QR(mergedOptions);

      qrCode
        .getRawData('png')
        .then((blob) => {
          if (blob instanceof Blob) {
            return URL.createObjectURL(blob);
          }
        })
        .then(setDataURL)
        .catch(console.log);
    };

    createQRCode();
  }, [data, maxSize, options]);

  if (!dataURL) {
    return (
      <Box
        aria-hidden
        aria-label={imgProps.alt || 'QR Code'}
        sx={{ width: size, height: size }}
      />
    );
  }
  return (
    <Box
      component='img'
      {...imgProps}
      alt={imgProps.alt || 'QR Code'}
      src={dataURL}
      sx={{
        width: size,
        height: size,
        filter: isOnDark ? 'invert()' : 'unset',
      }}
    />
  );
}
